﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// A page that is used to confirm the ownership of the users email address or phone number.

// Can be tested in jRåd.
// Register an account through self service and then login to it.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { TitleText } from "~base/Components/Primitives/TitleText";
import Grid from "~base/Components/Layout/Grid";
import { useIntl } from "~base/Internationalization/IntlContext";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import {
    getSpacingClasses,
    useJssSpacingStyles,
} from "~base/Components/Layout/JssSpacing";
import classnames from "classnames";
import TitleButton from "~base/Components/Primitives/TitleButton";
import { useStyles } from "./Styles";
import { reducer } from "./Store";
import { useAccountPostConfirm } from "~base/Services/API/Account/PostConfirm";
import { useSettings } from "~base/Settings/SettingsContext";
import Form from "~base/Components/Primitives/Form";
import ApplicationPage from "~base/Pages/Shared/ApplicationPage";
import { useSearchParams } from "react-router-dom";
import { TextBlock } from "~base/Components/Primitives/TextBlock";
import { ActionLink } from "~base/Components/Primitives/Link";
import { useAccountPostConfirmPhone } from "~base/Services/API/Account/PostConfirmPhone";
import { getErrorMessage } from "~base/Services/API";
import { useEffect } from "react";
import { useNavigateService } from "~base/Services/NavigateService";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type ConfirmProps = unknown;

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const ConfirmPage: React.FunctionComponent<ConfirmProps> = (
    props: ConfirmProps
) => {
    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("ConfirmPage"),
        reducer,
        {
            errorMessage: null,
            loading: false,
            state: "Default",
            confirmCode: null,
        }
    );
    const classes = useStyles();

    const settings = useSettings();
    const navigate = useNavigateService();
    const allowConfirmEmail = settings.valueAsBool("Account.AllowConfirmEmail");
    const allowConfirmPhone = settings.valueAsBool("Account.AllowConfirmPhone");
    const serverVirtualPath = settings.valueAsString("serverVirtualPath");
    const tenantName = settings.valueAsString("detectedTenantName");

    const [searchParams] = useSearchParams();
    const usernameFromQs = searchParams.get("username");
    const tenantPart = tenantName ? `/${tenantName}` : "";

    const loginUri = `${serverVirtualPath}Account/Login${tenantPart}?username=${encodeURIComponent(usernameFromQs)}`;

    const jssSpacingStyles = useJssSpacingStyles();

    const formBottomMarginClass = getSpacingClasses(jssSpacingStyles, null, {
        size: "XL",
        dir: "Bottom",
    });
    const formHorizontalMarginClass = getSpacingClasses(
        jssSpacingStyles,
        null,
        { size: "HalfMedium", dir: "Horizontal" }
    );

    const intl = useIntl();

    const [loadingAccountConfirmApi, invokeAccountConfirmApi] =
        useAccountPostConfirm({
            callback: async (status, response, _, requestData) => {
                if (status == "Success") {
                    const nextState =
                        requestData.Context == "Phone"
                            ? "ConfirmPhone"
                            : "ConfirmMail";
                    dispatch({ type: "setPageState", state: nextState });
                } else {
                    dispatch({
                        type: "setError",
                        errorMessage: await getErrorMessage(intl, response),
                    });
                }
            },
        });

    const [loadingAccountConfirmPhoneApi, invokeAccountConfirmPhoneApi] =
        useAccountPostConfirmPhone({
            callback: async (status, response, _, requestData) => {
                if (status == "Success") {
                    dispatch({ type: "navigate", uri: loginUri });
                } else {
                    dispatch({
                        type: "setError",
                        errorMessage: await getErrorMessage(intl, response),
                    });
                }
            },
        });

    useEffect(() => {
        if (state.navigateUri) {
            navigate.current(state.navigateUri);
        }
    }, [navigate, state.navigateUri]);

    const isLoading =
        loadingAccountConfirmApi ||
        loadingAccountConfirmPhoneApi ||
        state.loading;

    const hasMultipleConfirmMethods = allowConfirmEmail && allowConfirmPhone;

    return (
        <ApplicationPage isLoading={isLoading}>
            <Grid templateColumns="" width={"800px"}>
                <Rubrik
                    rubrikClassName={classes.title}
                    textCode="VisiFinance.UI.ConfirmAccount.Title"
                />

                {state.state == "Default" && (
                    <>
                        <TextBlock
                            textCode="VisiFinance.UI.ConfirmAccount.SubTitle"
                            cssClass={classnames(
                                formBottomMarginClass,
                                formHorizontalMarginClass
                            )}
                        />
                        {allowConfirmEmail && (
                            <TitleButton
                                testIdentifier="ConfirmEmailButton"
                                title=""
                                styleType={"DefaultPadded"}
                                onClick={() => {
                                    dispatch({ type: "setLoading" });
                                    invokeAccountConfirmApi({
                                        Context: "Email",
                                        Username: usernameFromQs,
                                        Tenant: tenantName,
                                    });
                                }}
                            >
                                {intl.formatText(
                                    "VisiFinance.UI.ConfirmAccount.EmailButton"
                                )}
                            </TitleButton>
                        )}
                        {allowConfirmPhone && (
                            <TitleButton
                                testIdentifier="ConfirmPhoneButton"
                                title=""
                                styleType={"DefaultPadded"}
                                onClick={() => {
                                    dispatch({ type: "setLoading" });
                                    invokeAccountConfirmApi({
                                        Context: "Phone",
                                        Username: usernameFromQs,
                                        Tenant: tenantName,
                                    });
                                }}
                            >
                                {intl.formatText(
                                    "VisiFinance.UI.ConfirmAccount.PhoneButton"
                                )}
                            </TitleButton>
                        )}
                    </>
                )}

                {state.state == "ConfirmMail" && (
                    <TextBlock
                        cssClass={classnames(
                            formBottomMarginClass,
                            formHorizontalMarginClass
                        )}
                        styleType="Paragraph"
                        textCode="VisiFinance.UI.ConfirmAccount.MailSentDescription"
                    />
                )}

                {state.state == "ConfirmPhone" && (
                    <>
                        <TextBlock
                            cssClass={classnames(
                                formBottomMarginClass,
                                formHorizontalMarginClass
                            )}
                            styleType="Paragraph"
                            textCode="VisiFinance.UI.ConfirmAccount.TextSentDescription"
                        />
                        <Form
                            testIdentifier="ConfirmPhoneCodeForm"
                            onSubmit={() => {
                                dispatch({ type: "setLoading" });
                                invokeAccountConfirmPhoneApi({
                                    Username: usernameFromQs,
                                    Code: state.confirmCode,
                                    Tenant: tenantName,
                                });
                            }}
                        >
                            <Grid className={classes.confirmPhoneGrid}>
                                <TitleText
                                    testIdentifier="VisiFinance.UI.ConfirmAccount.ConfirmationCode"
                                    titleTextCode="VisiFinance.UI.ConfirmAccount.ConfirmationCode"
                                    value={state.confirmCode}
                                    setValue={(x) =>
                                        dispatch({
                                            type: "setConfirmationCode",
                                            code: x,
                                        })
                                    }
                                    maxLength={6}
                                />

                                <TitleButton
                                    testIdentifier="ConfirmPhoneSubmitButton"
                                    title=" "
                                    styleType={"DefaultPadded"}
                                    submit={true}
                                >
                                    {intl.formatText(
                                        "VisiFinance.UI.ConfirmAccount.Submit"
                                    )}
                                </TitleButton>
                            </Grid>
                            {state.errorMessage && (
                                <TextBlock
                                    styleType="Paragraph"
                                    text={state.errorMessage}
                                    styleSubType="Validation"
                                    size="Small"
                                />
                            )}
                        </Form>
                    </>
                )}

                {(state.state == "ConfirmPhone" ||
                    state.state == "ConfirmMail") && (
                    <div className={classes.buttons}>
                        {hasMultipleConfirmMethods && (
                            <TitleButton
                                testIdentifier="ConfirmChangeMethodButton"
                                onClick={() =>
                                    dispatch({
                                        type: "setPageState",
                                        state: "Default",
                                    })
                                }
                                styleType={"DefaultPadded"}
                            >
                                {intl.formatText(
                                    "VisiFinance.UI.ConfirmAccount.ChangeConfirmationMethod"
                                )}
                            </TitleButton>
                        )}
                        <TitleButton
                            testIdentifier="ConfirmGoToLoginButton"
                            styleType={"DefaultPadded"}
                            onClick={() =>
                                dispatch({ type: "navigate", uri: loginUri })
                            }
                        >
                            {intl.formatText(
                                "VisiFinance.UI.ConfirmAccount.AlreadyVerifiedGoToLogin"
                            )}
                        </TitleButton>
                    </div>
                )}
            </Grid>
        </ApplicationPage>
    );
};
